import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  MessageCircle,
  Send,
  Users,
  Building2,
  Loader2,
  CheckCheck,
  Paperclip,
  Image as ImageIcon,
  X,
  Check,
  FileIcon,
  Download,
  Play,
  VolumeX,
  Handshake,
  TrendingUp,
  Pin,
  Trash2,
  Ban,
  Smile,
  BarChart3,
  SmilePlus,
  Plus,
  Sticker,
  ShieldAlert,
  ExternalLink,
  Pencil,
} from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAuth, type ChatMessage } from "@/contexts/AuthContext"
import { useChatWebSocket } from "@/hooks/useChatWebSocket"
import { EmojiPickerContent } from "@/components/dashboard/EmojiPicker"
import { GifPickerContent } from "@/components/dashboard/GifPicker"
import { StickerPickerContent, getStickerSrc } from "@/components/dashboard/StickerPicker"
import ovalMascot from "@/public/oval-pill.png"
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet"

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1,
    },
  },
}

const messageVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring" as const, bounce: 0.4 },
  },
}

const TOOLBAR_QUICK_REACTIONS = ["👍", "❤️", "😂", "🔥"]

function formatTime(dateString: string) {
  const date = new Date(dateString)
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()

  const time = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })

  if (isToday) {
    return time
  }

  return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} ${time}`
}

function MediaLightbox({
  src,
  alt,
  type,
  onClose,
}: {
  src: string
  alt: string
  type: "image" | "video"
  onClose: () => void
}) {
  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="relative max-w-[95vw] md:max-w-[90vw] max-h-[90vh]"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", bounce: 0.3 }}
        onClick={(e) => e.stopPropagation()}
      >
        {type === "video" ? (
          <video
            src={src}
            controls
            autoPlay
            playsInline
            className="max-w-full max-h-[80vh] md:max-h-[85vh] rounded-xl border-2 md:border-4 border-white shadow-2xl"
          />
        ) : (
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-[80vh] md:max-h-[85vh] rounded-xl border-2 md:border-4 border-white shadow-2xl object-contain"
          />
        )}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 md:-top-3 md:-right-3 p-2 bg-[#1C1C32] rounded-full border-2 md:border-3 border-[#2A2A45] shadow-[2px_2px_0_0_#1a1a1a] md:shadow-[3px_3px_0_0_#1a1a1a] hover:bg-[#1A1A30] transition-colors"
        >
          <X className="w-4 h-4 md:w-5 md:h-5 text-white" />
        </button>
      </motion.div>
    </motion.div>
  )
}

function PollTimeRemaining({ closesAt }: { closesAt: string | null }) {
  const [now, setNow] = React.useState(Date.now())

  React.useEffect(() => {
    if (!closesAt) return
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [closesAt])

  if (!closesAt) return null

  const remaining = new Date(closesAt).getTime() - now
  if (remaining <= 0) {
    return <span className="text-red-500 font-black uppercase text-[11px]">Closed</span>
  }

  const hours = Math.floor(remaining / (1000 * 60 * 60))
  const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((remaining % (1000 * 60)) / 1000)

  let timeStr: string
  if (hours > 24) {
    const days = Math.floor(hours / 24)
    timeStr = `${days}d ${hours % 24}h left`
  } else if (hours > 0) {
    timeStr = `${hours}h ${minutes}m left`
  } else if (minutes > 0) {
    timeStr = `${minutes}m ${seconds}s left`
  } else {
    timeStr = `${seconds}s left`
  }

  return <span className="text-amber-600 font-bold text-[11px]">{timeStr}</span>
}

function PollCard({
  message,
  onVote,
}: {
  message: ChatMessage
  onVote: (messageId: string, optionId: string) => Promise<void>
}) {
  if (!message.poll) return null

  const totalVotes = message.poll.totalVotes
  const maxVotes = Math.max(...message.poll.options.map((o) => o.votes), 0)
  const isClosed = message.poll.closesAt && new Date(message.poll.closesAt).getTime() < Date.now()

  return (
    <div className="mt-2 rounded-xl border-2 border-[#2A2A45]/50 bg-[#1C1C32]/85 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-black text-[#9A9ABF]">
          <BarChart3 className="w-3.5 h-3.5" /> Poll
          {message.poll.allowMultiSelect && (
            <span className="text-[10px] normal-case tracking-normal font-bold text-[#6A6A8A]">(multi-select)</span>
          )}
        </div>
        <PollTimeRemaining closesAt={message.poll.closesAt} />
      </div>
      <p className="text-xs md:text-sm font-bold text-white">{message.poll.question}</p>
      <div className="space-y-1.5">
        {message.poll.options.map((option) => {
          const percent = totalVotes > 0 ? Math.round((option.votes / totalVotes) * 100) : 0
          const isWinning = totalVotes > 0 && option.votes === maxVotes && maxVotes > 0
          return (
            <button
              key={option.id}
              onClick={() => !isClosed && onVote(message._id, option.id)}
              disabled={!!isClosed}
              className={`relative w-full text-left rounded-lg border-2 px-2.5 py-2 text-xs md:text-sm font-semibold transition overflow-hidden ${
                isClosed ? "opacity-80 cursor-default" : ""
              } ${
                option.selectedByCurrentUser
                  ? "border-[#F37020] bg-[#F37020]/10"
                  : isWinning
                  ? "border-green-300 bg-green-50/50"
                  : "border-[#2A2A45]/40 bg-[#1C1C32] hover:bg-[#1A1A30]"
              }`}
            >
              <div className="relative z-10 flex items-center justify-between gap-2">
                <span className="truncate flex items-center gap-1.5 text-white">
                  {option.emoji && <span className="text-sm">{option.emoji}</span>}
                  {option.text}
                </span>
                <span className="text-[11px] font-black text-white flex items-center gap-1 flex-shrink-0">
                  {percent}% <span className="text-[#9A9ABF]">({option.votes})</span>
                </span>
              </div>
              <motion.div
                className={`absolute inset-y-0 left-0 rounded-lg ${
                  isWinning ? "bg-green-200/40" : "bg-[#F37020]/15"
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${percent}%` }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              />
            </button>
          )
        })}
      </div>
      <p className="text-[11px] text-[#9A9ABF] font-semibold">{totalVotes} total votes</p>
    </div>
  )
}

// Desktop hover toolbar for messages
function MessageHoverToolbar({
  message,
  isAgencyAdmin,
  isOwn,
  placement,
  canEdit,
  onReact,
  onEdit,
  onPin,
  onDelete,
  onMute,
  showEmojiPicker,
  onToggleEmojiPicker,
}: {
  message: ChatMessage
  isAgencyAdmin: boolean
  isOwn: boolean
  placement: "above" | "below"
  canEdit: boolean
  onReact: (emoji: string) => void
  onEdit: () => void
  onPin: () => void
  onDelete: () => void
  onMute: () => void
  showEmojiPicker: boolean
  onToggleEmojiPicker: () => void
}) {
  const positionClass =
    placement === "below"
      ? (isOwn ? "right-1 top-full mt-1" : "left-1 top-full mt-1")
      : (isOwn ? "right-1 bottom-full mb-1" : "left-1 -top-4")

  return (
    <motion.div
      className={`absolute ${positionClass} z-30 flex items-center gap-0.5 px-1.5 py-1 bg-[#1C1C32] border-2 border-[#2A2A45] rounded-xl shadow-[3px_3px_0_0_#1a1a1a]`}
      initial={{ opacity: 0, y: 6, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.95 }}
      transition={{ duration: 0.12 }}
    >
      {TOOLBAR_QUICK_REACTIONS.map((emoji) => (
        <button
          key={emoji}
          onClick={() => onReact(emoji)}
          className="w-7 h-7 rounded-lg text-sm flex items-center justify-center hover:bg-[#F37020]/10 hover:scale-110 transition-all"
          title={`React ${emoji}`}
        >
          {emoji}
        </button>
      ))}
      <button
        onClick={onToggleEmojiPicker}
        className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
          showEmojiPicker ? "bg-[#F37020]/15 text-[#F37020]" : "hover:bg-[#1A1A30] text-[#6A6A8A]"
        }`}
        title="Add reaction"
      >
        <SmilePlus className="w-4 h-4" />
      </button>

      <div className="w-px h-5 bg-black/15 mx-0.5" />

      {canEdit && (
        <button
          onClick={onEdit}
          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#1A1A30] text-[#6A6A8A] transition-all"
          title="Edit"
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}

      {isAgencyAdmin && (
        <button
          onClick={onPin}
          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[#1A1A30] text-[#6A6A8A] transition-all"
          title={message.isPinned ? "Unpin" : "Pin"}
        >
          <Pin className="w-3.5 h-3.5" />
        </button>
      )}

      {(isAgencyAdmin || isOwn) && (
        <button
          onClick={onDelete}
          className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-red-50 text-red-500 transition-all"
          title="Delete"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}

      {isAgencyAdmin && !isOwn && (
        <button
          onClick={onMute}
          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
            message.senderMuted ? "hover:bg-green-50 text-green-500" : "hover:bg-amber-50 text-amber-500"
          }`}
          title={message.senderMuted ? "Unmute user" : "Mute user"}
        >
          <Ban className="w-3.5 h-3.5" />
        </button>
      )}
    </motion.div>
  )
}

// ─── Chat Avatar ─────────────────────────────────────
const AVATAR_COLORS = ["#F37020", "#32CD32", "#9B59B6", "#00CED1", "#FFD700", "#FF69B4"]

function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] ?? "#F37020"
}

function ChatAvatar({ message }: { message: ChatMessage }) {
  if (message.isSystem) {
    return (
      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-[#2A2A45] bg-[#F37020] flex-shrink-0 overflow-hidden shadow-[2px_2px_0_0_#1a1a1a]">
        <img src={ovalMascot} alt="System" className="w-full h-full object-cover" />
      </div>
    )
  }

  if (message.senderProfilePicture) {
    return (
      <div className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-[#2A2A45] flex-shrink-0 overflow-hidden shadow-[2px_2px_0_0_#1a1a1a]">
        <img src={message.senderProfilePicture} alt={message.senderName} className="w-full h-full object-cover" />
      </div>
    )
  }

  const color = getAvatarColor(message.senderName)
  return (
    <div
      className="w-7 h-7 md:w-8 md:h-8 rounded-full border-2 border-[#2A2A45] flex-shrink-0 flex items-center justify-center shadow-[2px_2px_0_0_#1a1a1a] text-white text-[10px] md:text-xs font-black"
      style={{ backgroundColor: color }}
    >
      {message.senderName.charAt(0).toUpperCase()}
    </div>
  )
}

function CTAButtons({ ctas }: { ctas: Array<{ label: string; url: string }> }) {
  const navigate = useNavigate()

  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {ctas.map((cta, i) => {
        const isInternal = cta.url.startsWith("/")
        return (
          <button
            key={i}
            onClick={() => {
              if (isInternal) {
                navigate(cta.url)
              } else {
                window.open(cta.url, "_blank", "noopener,noreferrer")
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1C1C32]/40 hover:bg-[#1C1C32]/50 text-white text-xs font-bold border border-white/20 backdrop-blur-sm transition-all hover:scale-[1.03] active:scale-95"
          >
            {cta.label}
            {!isInternal && <ExternalLink className="w-3 h-3 opacity-70" />}
          </button>
        )
      })}
    </div>
  )
}

function MessageBubble({
  message,
  isAgencyAdmin,
  onMediaClick,
  onReact,
  onEdit,
  onPin,
  onDelete,
  onMute,
  onVotePoll,
}: {
  message: ChatMessage
  isAgencyAdmin: boolean
  onMediaClick?: (src: string, alt: string, type: "image" | "video") => void
  onReact: (messageId: string, emoji: string) => Promise<void>
  onEdit: (messageId: string, content: string) => Promise<{ success: boolean; message?: string }>
  onPin: (messageId: string, pinned: boolean) => Promise<void>
  onDelete: (messageId: string) => Promise<void>
  onMute: (targetUserId: string, muted: boolean) => Promise<void>
  onVotePoll: (messageId: string, optionId: string) => Promise<void>
}) {
  const isImage = message.fileType?.startsWith("image/")
  const isVideo = message.fileType?.startsWith("video/")
  const isSystemMessage = message.isSystem
  const [isHovered, setIsHovered] = React.useState(false)
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false)
  const [emojiPickerPos, setEmojiPickerPos] = React.useState<{ top: number; left: number; maxHeight: number } | null>(null)
  const [mobileSheetOpen, setMobileSheetOpen] = React.useState(false)
  const [toolbarPlacement, setToolbarPlacement] = React.useState<"above" | "below">("above")
  const [isEditing, setIsEditing] = React.useState(false)
  const [editDraft, setEditDraft] = React.useState(message.content || "")
  const [editError, setEditError] = React.useState<string | null>(null)
  const [isSavingEdit, setIsSavingEdit] = React.useState(false)
  const longPressTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const bubbleRef = React.useRef<HTMLDivElement>(null)

  const keepToolbarOpen = isHovered || showEmojiPicker
  const canEditMessage =
    message.isOwn &&
    !message.isSystem &&
    !message.isDeleted &&
    message.messageType === "text" &&
    !message.fileUrl &&
    !message.gifUrl &&
    !message.sticker &&
    !message.poll

  const updateToolbarPlacement = React.useCallback(() => {
    if (!bubbleRef.current || typeof window === "undefined") return

    const rect = bubbleRef.current.getBoundingClientRect()
    const headerSafeTop = 100
    const estimatedToolbarHeight = 52
    const placeBelow = rect.top < headerSafeTop + estimatedToolbarHeight

    setToolbarPlacement(placeBelow ? "below" : "above")
  }, [])

  React.useEffect(() => {
    if (!keepToolbarOpen) return

    updateToolbarPlacement()
    if (typeof window === "undefined") return

    window.addEventListener("resize", updateToolbarPlacement)
    return () => window.removeEventListener("resize", updateToolbarPlacement)
  }, [keepToolbarOpen, updateToolbarPlacement])

  React.useEffect(() => {
    setEditDraft(message.content || "")
    setEditError(null)
    setIsSavingEdit(false)
    setIsEditing(false)
  }, [message._id])

  const handleTouchStart = () => {
    if (message.isDeleted) return
    longPressTimer.current = setTimeout(() => {
      setMobileSheetOpen(true)
    }, 500)
  }

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handleToolbarReact = (emoji: string) => {
    onReact(message._id, emoji)
    setShowEmojiPicker(false)
    setIsHovered(false)
  }

  const handleStartEdit = () => {
    if (!canEditMessage) return
    setEditDraft(message.content || "")
    setEditError(null)
    setShowEmojiPicker(false)
    setIsHovered(false)
    setMobileSheetOpen(false)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setEditDraft(message.content || "")
    setEditError(null)
    setIsSavingEdit(false)
    setIsEditing(false)
  }

  const handleSaveEdit = async () => {
    if (!canEditMessage || isSavingEdit) return
    const nextContent = editDraft.trim()
    if (!nextContent) {
      setEditError("Message cannot be empty.")
      return
    }
    if (nextContent === (message.content || "")) {
      setIsEditing(false)
      setEditError(null)
      return
    }

    setIsSavingEdit(true)
    setEditError(null)
    const result = await onEdit(message._id, nextContent)
    if (result.success) {
      setIsSavingEdit(false)
      setIsEditing(false)
      return
    }
    setEditError(result.message || "Failed to edit message.")
    setIsSavingEdit(false)
  }


  return (
    <>
      <motion.div
        className={`flex ${isSystemMessage ? "justify-start" : message.isOwn ? "justify-end" : "justify-start"} mb-3 md:mb-4`}
        variants={messageVariants}
        initial="hidden"
        animate="visible"
        onMouseEnter={() => {
          updateToolbarPlacement()
          setIsHovered(true)
        }}
        onMouseLeave={() => {
          if (!showEmojiPicker) setIsHovered(false)
        }}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchEnd}
      >
        {/* Avatar for non-own messages and system messages */}
        {(!message.isOwn || isSystemMessage) && (
          <div className="flex-shrink-0 self-end mb-5 mr-1.5 md:mr-2">
            <ChatAvatar message={message} />
          </div>
        )}

        <div
          ref={bubbleRef}
          className={`relative ${
            isSystemMessage
              ? "w-full max-w-[96%] md:max-w-[86%] order-1 text-left"
              : `max-w-[85%] md:max-w-[78%] ${message.isOwn ? "text-right" : "text-left"}`
          }`}
        >
          {/* Desktop hover toolbar */}
          <AnimatePresence>
            {keepToolbarOpen && !message.isDeleted && (
              <div className="hidden md:block">
                <MessageHoverToolbar
                  message={message}
                  isAgencyAdmin={isAgencyAdmin}
                  isOwn={message.isOwn}
                  placement={toolbarPlacement}
                  canEdit={canEditMessage}
                  onReact={handleToolbarReact}
                  onEdit={handleStartEdit}
                  onPin={() => onPin(message._id, !message.isPinned)}
                  onDelete={() => onDelete(message._id)}
                  onMute={() => onMute(message.senderId, !message.senderMuted)}
                  showEmojiPicker={showEmojiPicker}
                  onToggleEmojiPicker={() => {
                    if (!showEmojiPicker && bubbleRef.current) {
                      const rect = bubbleRef.current.getBoundingClientRect()
                      const desiredPickerH = 370
                      const minPickerH = 220
                      const pickerW = 352
                      const headerH = 132 // header + breathing room
                      const bottomPad = 160 // composer area
                      const viewportPad = 8
                      // Prefer above the message; if not enough room, show below; clamp to viewport.
                      const spaceAbove = Math.max(0, rect.top - headerH - viewportPad)
                      const spaceBelow = Math.max(0, window.innerHeight - rect.bottom - bottomPad - viewportPad)
                      let top: number
                      let maxHeight: number
                      if (spaceAbove >= desiredPickerH) {
                        maxHeight = desiredPickerH
                        top = rect.top - maxHeight
                      } else if (spaceBelow >= desiredPickerH) {
                        maxHeight = desiredPickerH
                        top = rect.bottom + 4
                      } else if (spaceBelow >= minPickerH && spaceBelow >= spaceAbove) {
                        maxHeight = spaceBelow
                        top = rect.bottom + 4
                      } else if (spaceAbove >= minPickerH) {
                        maxHeight = spaceAbove
                        top = rect.top - maxHeight
                      } else {
                        // Extremely constrained viewport: pin below header and shrink picker.
                        maxHeight = Math.max(minPickerH, window.innerHeight - headerH - bottomPad - viewportPad)
                        top = headerH
                      }
                      // Ensure it never goes above the header or below the viewport
                      top = Math.max(headerH, Math.min(top, window.innerHeight - maxHeight - viewportPad))
                      // Align to the side of the message
                      let left = message.isOwn ? rect.right - pickerW : rect.left
                      left = Math.max(viewportPad, Math.min(left, window.innerWidth - pickerW - viewportPad))
                      setEmojiPickerPos({ top, left, maxHeight })
                    }
                    setShowEmojiPicker((v) => !v)
                  }}
                />
              </div>
            )}
          </AnimatePresence>

          {/* Emoji picker for toolbar (desktop) — fixed to viewport */}
          {showEmojiPicker && emojiPickerPos && (
            <>
              <div
                className="fixed inset-0 z-[60]"
                onClick={() => {
                  setShowEmojiPicker(false)
                  setIsHovered(false)
                }}
              />
              <div
                className="fixed z-[70] hidden md:block"
                style={{ top: emojiPickerPos.top, left: emojiPickerPos.left }}
              >
                <EmojiPickerContent
                  onSelect={handleToolbarReact}
                  onClose={() => {
                    setShowEmojiPicker(false)
                    setIsHovered(false)
                  }}
                  maxHeight={emojiPickerPos.maxHeight}
                />
              </div>
            </>
          )}

          {(!message.isOwn || isSystemMessage) && (
            <div className="flex items-center gap-2 mb-1 ml-2 md:ml-3">
              <p className="text-[10px] md:text-xs text-[#9A9ABF] font-semibold">{message.senderName}</p>
              {isSystemMessage && (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-[#2A2A45] bg-[#F59E0B] text-black font-black uppercase tracking-wide">
                  <ShieldAlert className="w-3 h-3" />
                  SYSTEM
                </span>
              )}
              {!isSystemMessage && message.senderIsAgencyAdmin && (
                <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border border-[#F37020]/40 bg-[#F37020]/12 text-[#FDB37E] font-black uppercase tracking-wide">
                  ADMIN
                </span>
              )}
              {!isSystemMessage && message.senderMuted && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-amber-300 bg-amber-100 text-amber-700 font-bold uppercase tracking-wide">
                  muted
                </span>
              )}
            </div>
          )}
          <div className={`inline-flex max-w-full flex-col ${message.isOwn ? "items-end" : "items-start"}`}>
            <div
              className={`
                inline-block text-left rounded-xl
                ${
                  message.messageType === "sticker" && !message.isDeleted
                    ? "px-1 py-1"
                    : `px-2.5 py-1.5 md:px-3 md:py-2 border-2 border-[#2A2A45] ${
                        isSystemMessage
                          ? "bg-gradient-to-r from-[#111827] to-[#1F2937] text-white rounded-xl shadow-[3px_3px_0_0_#1a1a1a]"
                          : message.isOwn
                          ? "bg-[#F37020] text-white rounded-br-sm shadow-[2px_2px_0_0_#1a1a1a] md:shadow-[3px_3px_0_0_#1a1a1a]"
                          : "bg-[#1C1C32] text-white rounded-bl-sm shadow-[2px_2px_0_0_#1a1a1a] md:shadow-[3px_3px_0_0_#1a1a1a]"
                      }`
                }
              `}
            >
              {message.isPinned && (
                <div className={`mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-wide font-black ${isSystemMessage ? "text-amber-200" : message.isOwn ? "text-white/90" : "text-[#9A9ABF]"}`}>
                  <Pin className="w-3.5 h-3.5" /> Pinned
                </div>
              )}

              {message.isDeleted ? (
                <p className={`text-xs md:text-sm italic ${isSystemMessage ? "text-white/75" : message.isOwn ? "text-white/85" : "text-[#6A6A8A]"}`}>
                  This message was deleted.
                </p>
              ) : (
                <>
                  {message.messageType === "sticker" && message.sticker && (() => {
                    const stickerSrc = getStickerSrc(message.sticker!)
                    return stickerSrc ? (
                      <div className="mb-1">
                        <video
                          src={stickerSrc}
                          autoPlay
                          loop
                          muted
                          playsInline
                          className="w-32 h-32 md:w-40 md:h-40 object-contain"
                        />
                      </div>
                    ) : (
                      <div className="text-4xl md:text-5xl leading-none mb-2">{message.sticker}</div>
                    )
                  })()}

                  {message.messageType === "gif" && message.gifUrl && (
                    <button
                      onClick={() => onMediaClick?.(message.gifUrl!, "GIF", "image")}
                      className="block w-full text-left mb-2"
                    >
                      <img
                        src={message.gifUrl}
                        alt="GIF"
                        className="max-w-full rounded-lg border-2 border-[#2A2A45]/40 cursor-pointer hover:opacity-90 hover:scale-[1.02] transition-all max-h-[220px]"
                      />
                    </button>
                  )}

                  {message.fileUrl && (
                    <div className="mb-2">
                      {isImage ? (
                        <button
                          onClick={() => onMediaClick?.(message.fileUrl!, message.fileName || "Image", "image")}
                          className="block w-full text-left"
                        >
                          <img
                            src={message.fileUrl}
                            alt={message.fileName || "Image"}
                            className="max-w-full rounded-lg border-2 border-[#2A2A45]/40 cursor-pointer hover:opacity-90 hover:scale-[1.02] transition-all max-h-[220px] md:max-h-[300px]"
                          />
                        </button>
                    ) : isVideo ? (
                      <button
                        onClick={() => onMediaClick?.(message.fileUrl!, message.fileName || "Video", "video")}
                        className="block w-full text-left relative group"
                      >
                        <video
                          src={message.fileUrl}
                          className="max-w-full rounded-lg border-2 border-[#2A2A45]/40 cursor-pointer hover:opacity-90 transition-all max-h-[220px] md:max-h-[300px]"
                          autoPlay={isSystemMessage}
                          loop={isSystemMessage}
                          muted
                          playsInline
                          preload="metadata"
                        />
                        {isSystemMessage && (
                          <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/55 border border-white/15 text-white text-[10px] font-bold backdrop-blur-sm">
                            <VolumeX className="w-3 h-3" />
                            Muted
                          </div>
                        )}
                        {!isSystemMessage && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="p-3 md:p-4 bg-black/50 rounded-full group-hover:bg-black/70 transition-colors">
                              <Play className="w-6 h-6 md:w-8 md:h-8 text-white fill-white" />
                            </div>
                          </div>
                        )}
                      </button>
                      ) : (
                        <a
                          href={message.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-2 md:gap-3 p-2 md:p-3 rounded-xl border-2 border-[#2A2A45]/40 ${
                            isSystemMessage || message.isOwn ? "bg-[#1C1C32]/30" : "bg-[#1A1A30]"
                          } hover:bg-black/5 transition-colors`}
                        >
                          <div className="p-1.5 md:p-2 bg-[#1C1C32] rounded-lg border-2 border-[#2A2A45]/40 flex-shrink-0">
                            <FileIcon className="w-5 h-5 md:w-6 md:h-6 text-[#F37020]" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs md:text-sm font-bold truncate">{message.fileName || "File"}</p>
                            <p className="text-[10px] opacity-60 uppercase font-black">{message.fileType?.split("/")[1] || "FILE"}</p>
                          </div>
                          <Download className="w-4 h-4 md:w-5 md:h-5 opacity-60 flex-shrink-0" />
                        </a>
                      )}
                    </div>
                  )}

                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        value={editDraft}
                        onChange={(e) => {
                          setEditDraft(e.target.value)
                          if (editError) setEditError(null)
                        }}
                        rows={3}
                        maxLength={2000}
                        autoFocus
                        className={`w-full resize-y rounded-lg border-2 px-2.5 py-2 text-xs md:text-sm font-medium outline-none transition-colors ${
                          message.isOwn
                            ? "bg-white/15 border-white/25 text-white placeholder:text-white/60 focus:border-white/60"
                            : "bg-[#141426] border-[#2A2A45] text-white placeholder:text-[#6A6A8A] focus:border-[#F37020]/70"
                        }`}
                        placeholder="Edit your message..."
                      />
                      {editError && (
                        <div className={`text-[11px] font-semibold ${message.isOwn ? "text-amber-100" : "text-red-400"}`}>
                          {editError}
                        </div>
                      )}
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={isSavingEdit}
                          className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-bold transition-colors ${
                            message.isOwn
                              ? "border-white/25 text-white/90 hover:bg-white/10"
                              : "border-[#2A2A45] text-[#9A9ABF] hover:bg-[#1A1A30]"
                          } disabled:opacity-60`}
                        >
                          <X className="w-3 h-3" />
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveEdit}
                          disabled={isSavingEdit || editDraft.trim().length === 0}
                          className={`inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[11px] font-bold transition-colors disabled:opacity-60 ${
                            message.isOwn
                              ? "border-white/35 bg-white/90 text-[#1A1A30] hover:bg-white disabled:bg-white/15 disabled:text-white/55"
                              : "border-[#F37020]/40 bg-[#F37020]/15 text-[#F59E0B] hover:bg-[#F37020]/20 disabled:bg-[#F37020]/8 disabled:text-[#B98A55]"
                          }`}
                        >
                          {isSavingEdit ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    message.content && (
                      <p className="text-xs md:text-sm font-medium whitespace-pre-wrap">{message.content}</p>
                    )
                  )}

                  {message.isSystem && message.ctas && message.ctas.length > 0 && (
                    <CTAButtons ctas={message.ctas} />
                  )}

                  {message.messageType === "poll" && (
                    <PollCard message={message} onVote={onVotePoll} />
                  )}
                </>
              )}
            </div>

            {/* Reactions row + add reaction button */}
            <div className={`mt-1.5 w-full space-y-1 ${message.isOwn ? "pr-1" : ""}`}>
              {!message.isDeleted && (message.reactions.length > 0 || isHovered) && (
                <div className={`relative flex flex-wrap items-center gap-1.5 ${message.isOwn ? "justify-end" : "justify-start"}`}>
                  {message.reactions.map((reaction) => (
                    <button
                      key={`${message._id}-${reaction.emoji}`}
                      onClick={() => onReact(message._id, reaction.emoji)}
                      className={`group inline-flex items-center gap-0.5 px-1.5 py-px rounded-md border-2 border-[#2A2A45] text-xs font-black transition-all ${
                        reaction.reactedByCurrentUser
                          ? "bg-[#1C1C32] shadow-[2px_2px_0_0_#F37020] border-[#F37020]"
                          : "bg-[#1C1C32] text-white shadow-[2px_2px_0_0_#1a1a1a] hover:shadow-[3px_3px_0_0_#1a1a1a] hover:-translate-y-px"
                      }`}
                    >
                      <span className="text-xs leading-none">{reaction.emoji}</span>
                      <span className={`text-[11px] tabular-nums ${reaction.reactedByCurrentUser ? "text-[#F37020]" : "text-[#9A9ABF]"}`}>{reaction.count}</span>
                    </button>
                  ))}

                </div>
              )}

              {/* Timestamp row - clean, no action buttons */}
              <div className={`flex items-center gap-1.5 ${message.isOwn ? "justify-end" : "justify-start"}`}>
                <span className="text-[10px] md:text-xs font-semibold text-[#9A9ABF]">{formatTime(message.createdAt)}</span>
                {message.isEdited && !message.isDeleted && (
                  <span className="text-[10px] md:text-xs font-semibold text-[#6A6A8A]">(edited)</span>
                )}
                {message.isOwn && !isSystemMessage && <CheckCheck className="w-3 h-3 md:w-4 md:h-4 text-[#00CED1] drop-shadow-sm" />}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mobile long-press bottom sheet */}
      <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl border-t-3 border-[#2A2A45] p-0 md:hidden" showCloseButton={false}>
          <SheetTitle className="sr-only">Message Actions</SheetTitle>
          <div className="p-4 space-y-4">
            {/* Quick reactions */}
            <div className="flex items-center justify-center gap-3">
              {TOOLBAR_QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => {
                    onReact(message._id, emoji)
                    setMobileSheetOpen(false)
                  }}
                  className="w-11 h-11 rounded-xl border-2 border-[#2A2A45]/50 bg-[#1C1C32] text-xl flex items-center justify-center hover:bg-[#F37020]/10 active:scale-95 transition-all shadow-sm"
                >
                  {emoji}
                </button>
              ))}
            </div>

            <div className="h-px bg-black/10" />

            {/* Action buttons */}
            <div className="space-y-1">
              {canEditMessage && (
                <button
                  onClick={() => {
                    handleStartEdit()
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#1A1A30] active:bg-[#1A1A30] transition-colors"
                >
                  <Pencil className="w-5 h-5 text-[#9A9ABF]" />
                  <span className="text-sm font-semibold text-white">Edit Message</span>
                </button>
              )}

              {isAgencyAdmin && (
                <button
                  onClick={() => {
                    onPin(message._id, !message.isPinned)
                    setMobileSheetOpen(false)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-[#1A1A30] active:bg-[#1A1A30] transition-colors"
                >
                  <Pin className="w-5 h-5 text-[#9A9ABF]" />
                  <span className="text-sm font-semibold text-white">{message.isPinned ? "Unpin Message" : "Pin Message"}</span>
                </button>
              )}

              {(isAgencyAdmin || message.isOwn) && (
                <button
                  onClick={() => {
                    onDelete(message._id)
                    setMobileSheetOpen(false)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-red-50 active:bg-red-100 transition-colors"
                >
                  <Trash2 className="w-5 h-5 text-red-500" />
                  <span className="text-sm font-semibold text-red-600">Delete Message</span>
                </button>
              )}

              {isAgencyAdmin && !message.isOwn && (
                <button
                  onClick={() => {
                    onMute(message.senderId, !message.senderMuted)
                    setMobileSheetOpen(false)
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-amber-50 active:bg-amber-100 transition-colors"
                >
                  <Ban className={`w-5 h-5 ${message.senderMuted ? "text-green-500" : "text-amber-500"}`} />
                  <span className={`text-sm font-semibold ${message.senderMuted ? "text-green-600" : "text-amber-600"}`}>
                    {message.senderMuted ? "Unmute User" : "Mute User"}
                  </span>
                </button>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}

const POLL_DURATION_OPTIONS = [
  { label: "No limit", value: null },
  { label: "5 minutes", value: 5 * 60 * 1000 },
  { label: "15 minutes", value: 15 * 60 * 1000 },
  { label: "30 minutes", value: 30 * 60 * 1000 },
  { label: "1 hour", value: 60 * 60 * 1000 },
  { label: "6 hours", value: 6 * 60 * 60 * 1000 },
  { label: "12 hours", value: 12 * 60 * 60 * 1000 },
  { label: "24 hours", value: 24 * 60 * 60 * 1000 },
  { label: "3 days", value: 3 * 24 * 60 * 60 * 1000 },
  { label: "1 week", value: 7 * 24 * 60 * 60 * 1000 },
]

const POLL_OPTION_EMOJIS = ["🔴", "🔵", "🟢", "🟡", "🟣", "🟠", "⭐", "🎯", "🏆", "💎", "🎮", "🎵", "🍕", "🚀", "💡", "🔥", "❤️", "👍", "✅", "❌"]

function ChatInput({
  onSend,
  onUpload,
  disabled,
  muted,
  canCreatePoll,
  systemOnlyMode,
  systemOnlyUntil,
  systemOnlyReason,
}: {
  onSend: (
    message: string,
    options?: {
      fileData?: { fileUrl: string; fileName: string; fileType: string }
      messageType?: "text" | "gif" | "sticker" | "poll"
      gifUrl?: string
      sticker?: string
      poll?: {
        question: string
        options: Array<{ id?: string; text: string; emoji?: string | null }>
        allowMultiSelect?: boolean
        closesAt?: string | null
      }
    }
  ) => Promise<{ success: boolean; message?: string }>
  onUpload: (file: File) => Promise<{ fileUrl: string; fileName: string; fileType: string } | null>
  disabled?: boolean
  muted?: boolean
  canCreatePoll?: boolean
  systemOnlyMode?: boolean
  systemOnlyUntil?: string | null
  systemOnlyReason?: string | null
}) {
  const [message, setMessage] = React.useState("")
  const [isSending, setIsSending] = React.useState(false)
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null)
  const [isUploading, setIsUploading] = React.useState(false)

  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false)
  const [showGifPicker, setShowGifPicker] = React.useState(false)
  const [showStickerPicker, setShowStickerPicker] = React.useState(false)

  interface PollOptionDraft { text: string; emoji: string | null }
  const [showPollComposer, setShowPollComposer] = React.useState(false)
  const [pollQuestion, setPollQuestion] = React.useState("")
  const [pollOptions, setPollOptions] = React.useState<PollOptionDraft[]>(
    [{ text: "", emoji: null }, { text: "", emoji: null }]
  )
  const [pollDuration, setPollDuration] = React.useState<number | null>(null)
  const [pollAllowMulti, setPollAllowMulti] = React.useState(false)
  const [activeEmojiOptionIdx, setActiveEmojiOptionIdx] = React.useState<number | null>(null)
  const [composerError, setComposerError] = React.useState<string | null>(null)

  const fileInputRef = React.useRef<HTMLInputElement>(null)
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const closeAllPickers = () => {
    setShowEmojiPicker(false)
    setShowGifPicker(false)
    setShowStickerPicker(false)
    setShowPollComposer(false)
  }

  const resetComposer = () => {
    setMessage("")
    setSelectedFile(null)
    setPollQuestion("")
    setPollOptions([{ text: "", emoji: null }, { text: "", emoji: null }])
    setPollDuration(null)
    setPollAllowMulti(false)
    setActiveEmojiOptionIdx(null)
    closeAllPickers()
  }

  const canSend = React.useMemo(() => {
    if (showPollComposer) {
      const validOptions = pollOptions.filter((o) => o.text.trim().length > 0)
      return pollQuestion.trim().length > 0 && validOptions.length >= 2
    }

    return Boolean(message.trim() || selectedFile)
  }, [message, pollOptions, pollQuestion, selectedFile, showPollComposer])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!canSend || isSending || disabled || muted || systemOnlyMode) {
      return
    }

    setIsSending(true)

    try {
      let fileData: { fileUrl: string; fileName: string; fileType: string } | undefined

      if (selectedFile && !showPollComposer) {
        setIsUploading(true)
        const uploadResult = await onUpload(selectedFile)
        if (uploadResult) {
          fileData = uploadResult
        }
        setIsUploading(false)
      }

      if (showPollComposer) {
        const validOptions = pollOptions
          .filter((option) => option.text.trim().length > 0)
          .map((option) => ({ text: option.text.trim(), emoji: option.emoji }))

        const sendRes = await onSend("", {
          messageType: "poll",
          poll: {
            question: pollQuestion.trim(),
            options: validOptions,
            allowMultiSelect: pollAllowMulti,
            closesAt: pollDuration ? new Date(Date.now() + pollDuration).toISOString() : null,
          },
        })
        if (!sendRes.success) {
          setComposerError(sendRes.message || "Failed to send poll.")
          return
        }
      } else {
        const sendRes = await onSend(message.trim(), {
          messageType: "text",
          fileData,
        })
        if (!sendRes.success) {
          setComposerError(sendRes.message || "Failed to send message.")
          return
        }
      }

      setComposerError(null)
      resetComposer()
    } finally {
      setIsUploading(false)
      setIsSending(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const isVideo = file.type.startsWith("video/")
    const maxSize = isVideo ? 50 * 1024 * 1024 : 10 * 1024 * 1024

    if (file.size > maxSize) {
      alert(`File size must be less than ${isVideo ? "50MB" : "10MB"}`)
      return
    }

    closeAllPickers()
    setSelectedFile(file)
  }

  const handleEmojiSelect = (emoji: string) => {
    const textarea = textareaRef.current
    if (textarea) {
      const start = textarea.selectionStart ?? message.length
      const end = textarea.selectionEnd ?? message.length
      const newMessage = message.slice(0, start) + emoji + message.slice(end)
      setMessage(newMessage)
      // Restore cursor position after the inserted emoji
      requestAnimationFrame(() => {
        const pos = start + emoji.length
        textarea.setSelectionRange(pos, pos)
        textarea.focus()
      })
    } else {
      setMessage((prev) => prev + emoji)
    }
  }

  const handleGifSelect = async (gifUrl: string) => {
    setShowGifPicker(false)
    setIsSending(true)
    try {
      const sendRes = await onSend(message.trim(), {
        messageType: "gif",
        gifUrl,
      })
      if (sendRes.success) {
        setComposerError(null)
        setMessage("")
      } else {
        setComposerError(sendRes.message || "Failed to send GIF.")
      }
    } finally {
      setIsSending(false)
    }
  }

  const handleStickerSelect = async (stickerId: string) => {
    setShowStickerPicker(false)
    setIsSending(true)
    try {
      const sendRes = await onSend("", {
        messageType: "sticker",
        sticker: stickerId,
      })
      if (!sendRes.success) {
        setComposerError(sendRes.message || "Failed to send sticker.")
      } else {
        setComposerError(null)
      }
    } finally {
      setIsSending(false)
    }
  }

  const togglePollComposer = () => {
    if (!canCreatePoll) return
    const next = !showPollComposer
    closeAllPickers()
    setShowPollComposer(next)
    if (next) {
      setSelectedFile(null)
    }
  }

  return (
    <div className="border-t-3 border-[#2A2A45] bg-[#1C1C32] shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
      {systemOnlyMode && (
        <div className="px-4 py-2 border-b-2 border-red-200 bg-red-50 text-red-700 text-xs md:text-sm font-semibold">
          Chat is in system-only mode
          {systemOnlyUntil ? ` until ${new Date(systemOnlyUntil).toLocaleString()}` : ""}.
          {systemOnlyReason ? ` ${systemOnlyReason}` : ""}
        </div>
      )}
      {muted && (
        <div className="px-4 py-2 border-b-2 border-amber-200 bg-amber-50 text-amber-700 text-xs md:text-sm font-semibold">
          You are currently muted by an agency admin. You can still read messages.
        </div>
      )}
      {composerError && (
        <div className="px-4 py-2 border-b-2 border-red-200 bg-red-50 text-red-700 text-xs md:text-sm font-semibold">
          {composerError}
        </div>
      )}

      {/* Emoji picker panel */}
      {showEmojiPicker && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setShowEmojiPicker(false)} />
          <div className="absolute bottom-full left-2 mb-2 z-40">
            <EmojiPickerContent
              onSelect={handleEmojiSelect}
              onClose={() => setShowEmojiPicker(false)}
            />
          </div>
        </>
      )}

      {/* GIF picker panel */}
      {showGifPicker && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setShowGifPicker(false)} />
          <div className="absolute bottom-full left-2 mb-2 z-40">
            <GifPickerContent
              onSelect={handleGifSelect}
              onClose={() => setShowGifPicker(false)}
            />
          </div>
        </>
      )}

      {/* Sticker picker panel */}
      {showStickerPicker && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setShowStickerPicker(false)} />
          <div className="absolute bottom-full left-2 mb-2 z-40">
            <StickerPickerContent
              onSelect={handleStickerSelect}
              onClose={() => setShowStickerPicker(false)}
            />
          </div>
        </>
      )}

      {(showPollComposer || selectedFile) && (
        <div className="p-2 md:p-3 bg-[#1A1A30] border-b-2 border-[#2A2A45]/30 space-y-2">
          {selectedFile && (
            <div className="flex items-center gap-2 md:gap-3">
              <div className="p-1.5 md:p-2 bg-[#1C1C32] rounded-lg border-2 border-[#2A2A45]/40 flex-shrink-0">
                {selectedFile.type.startsWith("image/") ? (
                  <ImageIcon className="w-4 h-4 md:w-5 md:h-5 text-[#F37020]" />
                ) : selectedFile.type.startsWith("video/") ? (
                  <Play className="w-4 h-4 md:w-5 md:h-5 text-[#F37020]" />
                ) : (
                  <FileIcon className="w-4 h-4 md:w-5 md:h-5 text-[#F37020]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm font-bold truncate">{selectedFile.name}</p>
                <p className="text-[10px] text-[#6A6A8A] uppercase font-black">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                onClick={() => setSelectedFile(null)}
                className="p-1 hover:bg-[#222240] rounded-full transition-colors flex-shrink-0"
              >
                <X className="w-4 h-4 md:w-5 md:h-5 text-[#6A6A8A]" />
              </button>
            </div>
          )}

          {showPollComposer && (
            <div className="space-y-2">
              <input
                value={pollQuestion}
                onChange={(e) => setPollQuestion(e.target.value)}
                placeholder="Ask a question..."
                className="w-full rounded-lg border-2 border-[#2A2A45]/50 bg-[#1C1C32] px-3 py-2 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#F37020] focus:border-transparent"
                maxLength={500}
              />
              {pollOptions.map((option, idx) => (
                <div key={`poll-option-${idx}`} className="flex items-center gap-1.5">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setActiveEmojiOptionIdx(activeEmojiOptionIdx === idx ? null : idx)}
                      className="w-9 h-9 rounded-lg border-2 border-[#2A2A45]/50 bg-[#1C1C32] flex items-center justify-center text-sm hover:bg-[#1A1A30] transition-colors flex-shrink-0"
                      title="Add emoji icon"
                    >
                      {option.emoji || <Plus className="w-3.5 h-3.5 text-[#6A6A8A]" />}
                    </button>
                    {activeEmojiOptionIdx === idx && (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setActiveEmojiOptionIdx(null)} />
                        <div className="absolute bottom-full left-0 mb-1 z-40 bg-[#1C1C32] border-2 border-[#2A2A45] rounded-xl shadow-[3px_3px_0_0_#1a1a1a] p-2 w-[200px]">
                          <div className="grid grid-cols-5 gap-1">
                            {POLL_OPTION_EMOJIS.map((emoji) => (
                              <button
                                key={emoji}
                                type="button"
                                onClick={() => {
                                  setPollOptions((prev) =>
                                    prev.map((o, i) => i === idx ? { text: o.text, emoji: o.emoji === emoji ? null : emoji } : o)
                                  )
                                  setActiveEmojiOptionIdx(null)
                                }}
                                className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm hover:bg-[#F37020]/10 transition-colors ${
                                  option.emoji === emoji ? "bg-[#F37020]/15 ring-2 ring-[#F37020]" : ""
                                }`}
                              >
                                {emoji}
                              </button>
                            ))}
                          </div>
                          {option.emoji && (
                            <button
                              type="button"
                              onClick={() => {
                                setPollOptions((prev) =>
                                  prev.map((o, i) => i === idx ? { text: o.text, emoji: null } : o)
                                )
                                setActiveEmojiOptionIdx(null)
                              }}
                              className="w-full mt-1 text-[10px] font-bold text-[#6A6A8A] hover:text-red-500 transition-colors"
                            >
                              Remove icon
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    value={option.text}
                    onChange={(e) => {
                      const val = e.target.value
                      setPollOptions((prev) =>
                        prev.map((o, i) => i === idx ? { text: val, emoji: o.emoji } : o)
                      )
                    }}
                    placeholder={`Option ${idx + 1}`}
                    className="flex-1 rounded-lg border-2 border-[#2A2A45]/50 bg-[#1C1C32] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F37020] focus:border-transparent"
                    maxLength={200}
                  />
                  {pollOptions.length > 2 && (
                    <button
                      type="button"
                      onClick={() => setPollOptions((prev) => prev.filter((_, i) => i !== idx))}
                      className="p-1 hover:bg-[#222240] rounded-full transition-colors flex-shrink-0"
                    >
                      <X className="w-3.5 h-3.5 text-[#6A6A8A]" />
                    </button>
                  )}
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2">
                {pollOptions.length < 6 && (
                  <button
                    type="button"
                    onClick={() => setPollOptions((prev) => [...prev, { text: "", emoji: null }])}
                    className="text-xs px-2.5 py-1.5 rounded-lg border-2 border-dashed border-[#2A2A45]/50 bg-[#1C1C32] hover:bg-[#1A1A30] font-semibold text-[#9A9ABF] transition-colors"
                  >
                    + Add Option
                  </button>
                )}
                <div className="flex-1" />
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={pollAllowMulti}
                    onChange={(e) => setPollAllowMulti(e.target.checked)}
                    className="w-3.5 h-3.5 rounded accent-[#F37020]"
                  />
                  <span className="text-[11px] font-bold text-[#9A9ABF]">Multi-select</span>
                </label>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-[#9A9ABF]">Duration:</span>
                <select
                  value={pollDuration ?? ""}
                  onChange={(e) => setPollDuration(e.target.value === "" ? null : Number(e.target.value))}
                  className="text-xs font-semibold px-2 py-1.5 rounded-lg border-2 border-[#2A2A45]/50 bg-[#1C1C32] focus:outline-none focus:ring-2 focus:ring-[#F37020] cursor-pointer"
                >
                  {POLL_DURATION_OPTIONS.map((opt) => (
                    <option key={opt.label} value={opt.value ?? ""}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-2 pb-3 md:p-4">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
          className="hidden"
        />

        {/* Mobile: toolbar row above input */}
        <div className="flex md:hidden items-center gap-1.5 mb-1.5 px-0.5">
          <motion.button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isSending || isUploading || muted || systemOnlyMode || showPollComposer}
            className="p-2 rounded-lg border border-[#2A2A45]/50 bg-[#1C1C32] text-[#9A9ABF] hover:bg-[#1A1A30] disabled:opacity-50"
            whileTap={{ scale: 0.9 }}
          >
            <Paperclip className="w-4 h-4" />
          </motion.button>

          <motion.button
            type="button"
            onClick={() => {
              const next = !showGifPicker
              closeAllPickers()
              setShowGifPicker(next)
            }}
            disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
            className={`p-2 rounded-lg border border-[#2A2A45]/50 disabled:opacity-50 ${
              showGifPicker ? "bg-[#F37020] text-white border-[#F37020]" : "bg-[#1C1C32] text-[#9A9ABF]"
            }`}
            whileTap={{ scale: 0.9 }}
          >
            <ImageIcon className="w-4 h-4" />
          </motion.button>

          <motion.button
            type="button"
            onClick={() => {
              const next = !showEmojiPicker
              closeAllPickers()
              setShowEmojiPicker(next)
            }}
            disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
            className={`p-2 rounded-lg border border-[#2A2A45]/50 disabled:opacity-50 ${
              showEmojiPicker ? "bg-[#F37020] text-white border-[#F37020]" : "bg-[#1C1C32] text-[#9A9ABF]"
            }`}
            whileTap={{ scale: 0.9 }}
          >
            <Smile className="w-4 h-4" />
          </motion.button>

          <motion.button
            type="button"
            onClick={() => {
              const next = !showStickerPicker
              closeAllPickers()
              setShowStickerPicker(next)
            }}
            disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
            className={`p-2 rounded-lg border border-[#2A2A45]/50 disabled:opacity-50 ${
              showStickerPicker ? "bg-[#F37020] text-white border-[#F37020]" : "bg-[#1C1C32] text-[#9A9ABF]"
            }`}
            whileTap={{ scale: 0.9 }}
          >
            <Sticker className="w-4 h-4" />
          </motion.button>

          {canCreatePoll && (
            <motion.button
              type="button"
              onClick={togglePollComposer}
              disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
              className={`p-2 rounded-lg border border-[#2A2A45]/50 disabled:opacity-50 ${
                showPollComposer ? "bg-[#F37020] text-white border-[#F37020]" : "bg-[#1C1C32] text-[#9A9ABF]"
              }`}
              whileTap={{ scale: 0.9 }}
            >
              <BarChart3 className="w-4 h-4" />
            </motion.button>
          )}
        </div>

        {/* Desktop: inline row; Mobile: just input + send */}
        <div className="flex items-end gap-2 md:gap-3">
          {/* Desktop-only toolbar buttons */}
          <motion.button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isSending || isUploading || muted || systemOnlyMode || showPollComposer}
            className="hidden md:flex p-3 rounded-xl border-3 border-[#2A2A45] shadow-[4px_4px_0_0_#1a1a1a] bg-[#1C1C32] text-[#9A9ABF] hover:bg-[#1A1A30] disabled:opacity-50 flex-shrink-0 items-center justify-center"
            whileHover={{ scale: 1.05, boxShadow: "6px 6px 0 0 #1a1a1a" }}
            whileTap={{ scale: 0.95, boxShadow: "2px 2px 0 0 #1a1a1a" }}
          >
            <Paperclip className="w-5 h-5" />
          </motion.button>

          <motion.button
            type="button"
            onClick={() => {
              const next = !showGifPicker
              closeAllPickers()
              setShowGifPicker(next)
            }}
            disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
            className={`hidden md:flex p-3 rounded-xl border-3 border-[#2A2A45] shadow-[4px_4px_0_0_#1a1a1a] disabled:opacity-50 flex-shrink-0 items-center justify-center ${
              showGifPicker ? "bg-[#F37020] text-white" : "bg-[#1C1C32] text-[#9A9ABF]"
            }`}
            whileHover={{ scale: 1.05, boxShadow: "6px 6px 0 0 #1a1a1a" }}
            whileTap={{ scale: 0.95, boxShadow: "2px 2px 0 0 #1a1a1a" }}
          >
            <ImageIcon className="w-5 h-5" />
          </motion.button>

          <motion.button
            type="button"
            onClick={() => {
              const next = !showEmojiPicker
              closeAllPickers()
              setShowEmojiPicker(next)
            }}
            disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
            className={`hidden md:flex p-3 rounded-xl border-3 border-[#2A2A45] shadow-[4px_4px_0_0_#1a1a1a] disabled:opacity-50 flex-shrink-0 items-center justify-center ${
              showEmojiPicker ? "bg-[#F37020] text-white" : "bg-[#1C1C32] text-[#9A9ABF]"
            }`}
            whileHover={{ scale: 1.05, boxShadow: "6px 6px 0 0 #1a1a1a" }}
            whileTap={{ scale: 0.95, boxShadow: "2px 2px 0 0 #1a1a1a" }}
          >
            <Smile className="w-5 h-5" />
          </motion.button>

          <motion.button
            type="button"
            onClick={() => {
              const next = !showStickerPicker
              closeAllPickers()
              setShowStickerPicker(next)
            }}
            disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
            className={`hidden md:flex p-3 rounded-xl border-3 border-[#2A2A45] shadow-[4px_4px_0_0_#1a1a1a] disabled:opacity-50 flex-shrink-0 items-center justify-center ${
              showStickerPicker ? "bg-[#F37020] text-white" : "bg-[#1C1C32] text-[#9A9ABF]"
            }`}
            whileHover={{ scale: 1.05, boxShadow: "6px 6px 0 0 #1a1a1a" }}
            whileTap={{ scale: 0.95, boxShadow: "2px 2px 0 0 #1a1a1a" }}
          >
            <Sticker className="w-5 h-5" />
          </motion.button>

          {canCreatePoll && (
            <motion.button
              type="button"
              onClick={togglePollComposer}
              disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
              className={`hidden md:flex p-3 rounded-xl border-3 border-[#2A2A45] shadow-[4px_4px_0_0_#1a1a1a] disabled:opacity-50 flex-shrink-0 items-center justify-center ${
                showPollComposer ? "bg-[#F37020] text-white" : "bg-[#1C1C32] text-[#9A9ABF]"
              }`}
              whileHover={{ scale: 1.05, boxShadow: "6px 6px 0 0 #1a1a1a" }}
              whileTap={{ scale: 0.95, boxShadow: "2px 2px 0 0 #1a1a1a" }}
            >
              <BarChart3 className="w-5 h-5" />
            </motion.button>
          )}

          <div className="flex-1 relative min-w-0">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                muted
                  ? "Muted by admin"
                  : systemOnlyMode
                  ? "System-only mode enabled by admin"
                  : showPollComposer
                  ? "Optional poll note"
                  : isUploading
                  ? "Uploading..."
                  : "Type a message..."
              }
              rows={1}
              disabled={disabled || isSending || isUploading || muted || systemOnlyMode}
              className="w-full px-3 md:px-4 py-2.5 md:py-3 bg-[#1A1A30] border-2 md:border-3 border-[#2A2A45] rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#F37020] font-medium disabled:opacity-50 text-sm md:text-base"
              style={{ minHeight: "44px", maxHeight: "120px" }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  handleSubmit(e)
                }
              }}
            />
          </div>

          <motion.button
            type="submit"
            disabled={!canSend || isSending || isUploading || disabled || muted || systemOnlyMode}
            className={`
              p-2.5 md:p-3 rounded-xl border-2 md:border-3 border-[#2A2A45] shadow-[3px_3px_0_0_#1a1a1a] md:shadow-[4px_4px_0_0_#1a1a1a]
              transition-all duration-200 flex-shrink-0
              ${canSend && !isSending && !isUploading && !disabled && !muted && !systemOnlyMode ? "bg-[#F37020] text-white" : "bg-[#222240] text-[#6A6A8A]"}
            `}
            whileHover={canSend && !isSending && !isUploading && !disabled && !muted && !systemOnlyMode ? { scale: 1.05, boxShadow: "6px 6px 0 0 #1a1a1a" } : {}}
            whileTap={canSend && !isSending && !isUploading && !disabled && !muted && !systemOnlyMode ? { scale: 0.95, boxShadow: "2px 2px 0 0 #1a1a1a" } : {}}
          >
            {isSending || isUploading ? <Loader2 className="w-4 h-4 md:w-5 md:h-5 animate-spin" /> : <Send className="w-4 h-4 md:w-5 md:h-5" />}
          </motion.button>
        </div>
      </form>
    </div>
  )
}

function NoAgencyState() {
  return (
    <motion.div
      className="flex-1 flex items-center justify-center p-8"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
    >
      <div className="text-center max-w-md">
        <motion.div
          className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-[#00CED1] to-[#0099AA] flex items-center justify-center border-4 border-[#2A2A45] shadow-[6px_6px_0_0_#1a1a1a]"
          animate={{ rotate: [0, 5, -5, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <MessageCircle className="w-12 h-12 text-white" />
        </motion.div>

        <h3 className="font-display text-2xl text-white text-shadow-retro-sm mb-3">No Agency Chat Available</h3>
        <p className="text-[#CCCCCC] font-medium mb-6">You are not currently part of an agency. Join an agency to access team chat.</p>

        <motion.div
          className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 border-2 border-cyan-400 rounded-full"
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <Building2 className="w-4 h-4 text-cyan-600" />
          <span className="text-sm font-bold text-cyan-700">Solo agents cannot access team chat</span>
        </motion.div>
      </div>
    </motion.div>
  )
}

function LoadingState() {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#6A6A8A] mx-auto mb-3" />
        <p className="text-[#6A6A8A] font-medium">Loading messages...</p>
      </div>
    </div>
  )
}

interface AgencyStats {
  memberCount: number
  dealsToday: number
  dealsThisWeek: number
  onlineCount: number
}

export function ChatTab() {
  const {
    user,
    getMessages,
    sendChatMessage,
    reactToChatMessage,
    editChatMessage,
    pinChatMessage,
    deleteChatMessage,
    muteChatUser,
    voteChatPoll,
    getPresignedUrl,
    token,
  } = useAuth()

  const [messages, setMessages] = React.useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [lightboxMedia, setLightboxMedia] = React.useState<{ src: string; alt: string; type: "image" | "video" } | null>(null)
  const [agencyStats, setAgencyStats] = React.useState<AgencyStats | null>(null)
  const [isAgencyAdmin, setIsAgencyAdmin] = React.useState(false)
  const [currentUserMuted, setCurrentUserMuted] = React.useState(false)
  const [mutedUserIds, setMutedUserIds] = React.useState<string[]>([])
  const [chatSystemOnlyMode, setChatSystemOnlyMode] = React.useState(false)
  const [chatSystemOnlyUntil, setChatSystemOnlyUntil] = React.useState<string | null>(null)
  const [chatSystemOnlyReason, setChatSystemOnlyReason] = React.useState<string | null>(null)

  const messagesEndRef = React.useRef<HTMLDivElement>(null)

  const userType = user?.onboardingData?.userType
  const isAgent = userType === "agent"
  const agencyId = user?.onboardingData?.agencyId || user?.generatedAgencyId
  const agencyName = user?.onboardingData?.agencyName

  const scrollToBottom = React.useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [])

  const applyMessageUpdate = React.useCallback((updatedMessage: ChatMessage) => {
    setMessages((prev) => prev.map((msg) => (msg._id === updatedMessage._id ? updatedMessage : msg)))
  }, [])

  React.useEffect(() => {
    if (!agencyId || !token) return

    const fetchStats = async () => {
      try {
        const response = await fetch(`/api/agency/stats?agencyId=${encodeURIComponent(agencyId)}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        const data = await response.json()
        if (data.success && data.stats) {
          setAgencyStats(data.stats)
        }
      } catch (err) {
        console.error("Failed to fetch agency stats:", err)
      }
    }

    fetchStats()
    const interval = setInterval(fetchStats, 60000)
    return () => clearInterval(interval)
  }, [agencyId, token])

  const fetchAllMessages = React.useCallback(async () => {
    if (!agencyId) return
    const result = await getMessages(agencyId)

    if (result.success && result.messages) {
      const sortedMessages = [...result.messages].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      setMessages(sortedMessages)
      setIsAgencyAdmin(Boolean(result.isAgencyAdmin))
      setCurrentUserMuted(Boolean(result.currentUserMuted))
      setMutedUserIds(result.mutedUserIds || [])
      setChatSystemOnlyMode(Boolean(result.chatSystemOnlyMode))
      setChatSystemOnlyUntil(result.chatSystemOnlyUntil || null)
      setChatSystemOnlyReason(result.chatSystemOnlyReason || null)
      setError(null)
    } else {
      setError(result.message || "Failed to load messages")
    }

    setIsLoading(false)
  }, [agencyId, getMessages])

  // Initial HTTP fetch only (no polling — WebSocket handles live updates)
  React.useEffect(() => {
    if (!agencyId) return
    fetchAllMessages()
  }, [agencyId, fetchAllMessages])

  const initialLoadRef = React.useRef(true)
  React.useEffect(() => {
    if (initialLoadRef.current && !isLoading && messages.length > 0) {
      // Wait for DOM to render the message list, then snap to bottom
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "instant" })
      }, 50)
      initialLoadRef.current = false
    }
  }, [isLoading, messages])

  // ---- WebSocket real-time updates ----

  const handleWSNewMessage = React.useCallback(
    (msg: ChatMessage) => {
      setMessages((prev) => {
        if (prev.some((m) => m._id === msg._id)) return prev
        return [...prev, msg]
      })
      setTimeout(() => scrollToBottom(), 100)
    },
    [scrollToBottom]
  )

  const handleWSUpdateMessage = React.useCallback((msg: ChatMessage) => {
    setMessages((prev) => prev.map((m) => (m._id === msg._id ? msg : m)))
  }, [])

  const handleWSDeleteMessage = React.useCallback((messageId: string) => {
    setMessages((prev) =>
      prev.map((m) =>
        m._id === messageId
          ? {
              ...m,
              isDeleted: true,
              content: "",
              fileUrl: undefined,
              fileName: undefined,
              fileType: undefined,
              gifUrl: undefined,
              sticker: undefined,
              poll: undefined,
              reactions: [],
              isPinned: false,
            }
          : m
      )
    )
  }, [])

  const handleWSPinChanged = React.useCallback(
    (msg: ChatMessage, unpinnedMessageId?: string) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (m._id === msg._id) return msg
          if (m._id === unpinnedMessageId) return { ...m, isPinned: false }
          return m
        })
      )
    },
    []
  )

  const handleWSMuteChanged = React.useCallback(
    (newMutedUserIds: string[]) => {
      setMutedUserIds(newMutedUserIds)
      setCurrentUserMuted(
        !isAgencyAdmin && !!user?._id && newMutedUserIds.includes(user._id)
      )
    },
    [isAgencyAdmin, user?._id]
  )

  useChatWebSocket({
    token,
    agencyId: agencyId || null,
    userId: user?._id || null,
    mutedUserIds,
    onNewMessage: handleWSNewMessage,
    onUpdateMessage: handleWSUpdateMessage,
    onDeleteMessage: handleWSDeleteMessage,
    onPinChanged: handleWSPinChanged,
    onMuteChanged: handleWSMuteChanged,
    onReconnect: fetchAllMessages,
  })

  const handleSendMessage = async (
    content: string,
    options?: {
      fileData?: { fileUrl: string; fileName: string; fileType: string }
      messageType?: "text" | "gif" | "sticker" | "poll"
      gifUrl?: string
      sticker?: string
      poll?: {
        question: string
        options: Array<{ id?: string; text: string; emoji?: string | null }>
        allowMultiSelect?: boolean
        closesAt?: string | null
      }
    }
  ): Promise<{ success: boolean; message?: string }> => {
    if (!agencyId) return { success: false, message: "Missing agency ID" }

    const result = await sendChatMessage(agencyId, content, options)
    if (result.success && result.chatMessage) {
      setMessages((prev) => {
        if (prev.some((m) => m._id === result.chatMessage!._id)) return prev
        return [...prev, result.chatMessage!]
      })
      setTimeout(() => scrollToBottom(), 100)
      return { success: true }
    }

    if (result.message?.toLowerCase().includes("system-only mode")) {
      await fetchAllMessages()
    }
    return { success: false, message: result.message || "Failed to send message" }
  }

  const handleFileUpload = async (file: File) => {
    try {
      const result = await getPresignedUrl(file.name, file.type)
      if (result.success && result.uploadUrl && result.fileUrl) {
        const uploadResponse = await fetch(result.uploadUrl, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        })

        if (uploadResponse.ok) {
          return {
            fileUrl: result.fileUrl,
            fileName: file.name,
            fileType: file.type,
          }
        }
      }

      return null
    } catch (err) {
      console.error("File upload error:", err)
      return null
    }
  }

  const handleReact = async (messageId: string, emoji: string) => {
    if (!agencyId) return

    const result = await reactToChatMessage(agencyId, messageId, emoji)
    if (result.success && result.chatMessage) {
      applyMessageUpdate(result.chatMessage)
    }
  }

  const handlePin = async (messageId: string, pinned: boolean) => {
    if (!agencyId) return

    const result = await pinChatMessage(agencyId, messageId, pinned)
    if (result.success && result.chatMessage) {
      setMessages((prev) =>
        prev.map((message) => {
          if (message._id === result.chatMessage!._id) {
            return result.chatMessage!
          }
          if (pinned && message.isPinned) {
            return { ...message, isPinned: false }
          }
          return message
        })
      )
    }
  }

  const handleEdit = async (messageId: string, content: string) => {
    if (!agencyId) return { success: false, message: "Missing agency ID" }

    const result = await editChatMessage(agencyId, messageId, content)
    if (result.success && result.chatMessage) {
      applyMessageUpdate(result.chatMessage)
      return { success: true }
    }

    return { success: false, message: result.message || "Failed to edit message" }
  }

  const handleDelete = async (messageId: string) => {
    if (!agencyId) return

    const result = await deleteChatMessage(agencyId, messageId)
    if (result.success && result.chatMessage) {
      applyMessageUpdate(result.chatMessage)
    }
  }

  const handleMute = async (targetUserId: string, muted: boolean) => {
    if (!agencyId) return

    const result = await muteChatUser(agencyId, targetUserId, muted)
    if (result.success && result.mutedUserIds) {
      setMutedUserIds(result.mutedUserIds)
      setMessages((prev) =>
        prev.map((message) => ({
          ...message,
          senderMuted: result.mutedUserIds!.includes(message.senderId),
        }))
      )
    }
  }

  const handleVotePoll = async (messageId: string, optionId: string) => {
    if (!agencyId) return

    const result = await voteChatPoll(agencyId, messageId, optionId)
    if (result.success && result.chatMessage) {
      applyMessageUpdate(result.chatMessage)
    }
  }

  const pinnedMessage = React.useMemo(
    () => messages.find((message) => message.isPinned && !message.isDeleted),
    [messages]
  )

  if (!isAgent && userType !== "agency") {
    return (
      <motion.div className="h-full flex flex-col" variants={containerVariants} initial="hidden" animate="visible">
        <NoAgencyState />
      </motion.div>
    )
  }

  if (!agencyId) {
    return (
      <motion.div className="h-full flex flex-col" variants={containerVariants} initial="hidden" animate="visible">
        <NoAgencyState />
      </motion.div>
    )
  }

  return (
    <motion.div className="relative h-full" variants={containerVariants} initial="hidden" animate="visible">
      <div className="absolute top-0 left-0 right-0 p-3 md:p-4 border-b-3 border-[#2A2A45] bg-[#1C1C32] shadow-sm z-20">
        <div className="flex items-center justify-between gap-2 md:gap-4">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            <motion.div
              className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-[#00CED1] to-[#0099AA] flex items-center justify-center border-2 border-[#2A2A45] flex-shrink-0"
              whileHover={{ scale: 1.05, rotate: 5 }}
            >
              <Users className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </motion.div>
            <div className="min-w-0">
              <h2 className="font-bold text-white text-sm md:text-base truncate">{agencyName || agencyId}</h2>
              <div className="flex items-center gap-1 md:gap-2 text-xs md:text-sm text-[#6A6A8A]">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
                <span>{agencyStats?.onlineCount || 1} online</span>
                <span className="text-[#6A6A8A]">•</span>
                <span>{agencyStats?.memberCount || 1} members</span>
                {isAgencyAdmin && (
                  <>
                    <span className="text-[#6A6A8A]">•</span>
                    <span className="font-bold text-[#F37020]">Admin</span>
                  </>
                )}
                {currentUserMuted && (
                  <>
                    <span className="text-[#6A6A8A]">•</span>
                    <span className="font-bold text-amber-600">Muted</span>
                  </>
                )}
                {chatSystemOnlyMode && (
                  <>
                    <span className="text-[#6A6A8A]">•</span>
                    <span className="font-bold text-red-600">System-only mode</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-2 flex-shrink-0">
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border-2 border-green-200 rounded-full">
              <Handshake className="w-4 h-4 text-green-600" />
              <span className="text-sm font-bold text-green-700">{agencyStats?.dealsToday || 0} today</span>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border-2 border-purple-200 rounded-full">
              <TrendingUp className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-bold text-purple-700">{agencyStats?.dealsThisWeek || 0} this week</span>
            </div>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="absolute inset-0 pt-[72px] md:pt-[88px] pb-[120px] md:pb-[140px] flex items-center justify-center">
          <LoadingState />
        </div>
      ) : error ? (
        <div className="absolute inset-0 pt-[72px] md:pt-[88px] pb-[120px] md:pb-[140px] flex items-center justify-center p-4 md:p-8">
          <div className="text-center">
            <p className="text-red-500 font-medium">{error}</p>
          </div>
        </div>
      ) : (
        <div className="absolute inset-0 z-10 overflow-y-auto overflow-x-hidden px-3 md:px-4 pt-[76px] md:pt-[96px] pb-[130px] md:pb-[150px] bg-gradient-to-b from-[#161628]/30 to-[#161628]/10">
          {pinnedMessage && (
            <div className="mb-3 p-2.5 rounded-xl border-2 border-[#2A2A45] bg-yellow-50 shadow-[2px_2px_0_0_#1a1a1a]">
              <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-wide text-amber-700">
                <Pin className="w-3.5 h-3.5" /> Pinned Message
              </div>
              <p className="text-xs md:text-sm font-semibold text-white mt-1 truncate">
                {pinnedMessage.content || (pinnedMessage.messageType === "poll" ? pinnedMessage.poll?.question : `${pinnedMessage.senderName} shared a ${pinnedMessage.messageType}`)}
              </p>
            </div>
          )}

          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            {messages.map((message) => (
              <MessageBubble
                key={message._id}
                message={{
                  ...message,
                  senderMuted: mutedUserIds.includes(message.senderId),
                }}
                isAgencyAdmin={isAgencyAdmin}
                onMediaClick={(src, alt, type) => setLightboxMedia({ src, alt, type })}
                onReact={handleReact}
                onEdit={handleEdit}
                onPin={handlePin}
                onDelete={handleDelete}
                onMute={handleMute}
                onVotePoll={handleVotePoll}
              />
            ))}
          </motion.div>
          <div ref={messagesEndRef} />

          {messages.length === 0 && (
            <motion.div className="text-center mt-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
              <motion.div
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#1C1C32] border-2 border-dashed border-[#2A2A45] rounded-full"
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <MessageCircle className="w-4 h-4 text-[#6A6A8A]" />
                <span className="text-sm text-[#6A6A8A]">Start a conversation with your team!</span>
              </motion.div>
            </motion.div>
          )}
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 z-20">
        <ChatInput
          onSend={handleSendMessage}
          onUpload={handleFileUpload}
          disabled={isLoading || !!error}
          muted={currentUserMuted}
          canCreatePoll={isAgencyAdmin}
          systemOnlyMode={chatSystemOnlyMode}
          systemOnlyUntil={chatSystemOnlyUntil}
          systemOnlyReason={chatSystemOnlyReason}
        />
      </div>

      <AnimatePresence>
        {lightboxMedia && (
          <MediaLightbox
            src={lightboxMedia.src}
            alt={lightboxMedia.alt}
            type={lightboxMedia.type}
            onClose={() => setLightboxMedia(null)}
          />
        )}
      </AnimatePresence>
    </motion.div>
  )
}
